import React from "react";
import { Link } from "react-router-dom";
import styles from "./HotelList.module.css";
import logoDark from "../../Images/logo 3.svg";
import logoLight from "../../Images/favicon.png";
import heroImage from "../../Images/Home/booking-engine-hero.png";
import customizableWebsiteImage from "../../Images/Home/customizable-website.png";
import directReservationImage from "../../Images/Home/direct-reservation.png";
import {
  CONTACT_EMAIL,
  CONTACT_MOBILE,
  CONTACT_MOBILE_LABEL,
  CONTACT_US_URL,
} from "../../utility/contactInfo";

const HERO_HIGHLIGHTS = [
  { icon: "fa-regular fa-calendar-check", label: "Real-time availability" },
  { icon: "fa-solid fa-bolt", label: "Instant confirmation" },
  { icon: "fa-solid fa-sliders", label: "Custom booking options" },
  { icon: "fa-solid fa-tags", label: "Promotions & offers" },
];

const FEATURES = [
  {
    image: customizableWebsiteImage,
    background: "#ffecd8",
    icon: "fa-solid fa-palette",
    title: "Customizable Website",
    text: "Create a personalized booking experience with our customizable website templates. Tailor the look and feel to match your brand, offer special promotions, and provide guests with real-time availability and instant confirmation directly from your site.",
    points: [
      "Templates that match your brand",
      "Special promotions for guests",
      "Real-time availability on your site",
    ],
  },
  {
    image: directReservationImage,
    background: "#e9f4ff",
    icon: "fa-solid fa-bell-concierge",
    title: "Direct Reservation",
    text: "Offer a seamless direct booking experience to your guests. Enable them to reserve rooms directly from your website with ease, accessing real-time availability and receiving instant confirmations.",
    points: [
      "Guests book straight from your website",
      "Live room availability",
      "Instant booking confirmations",
    ],
  },
];

const TESTIMONIALS = [
  {
    text: "Managing a large restaurant chain is a breeze with Posease.",
    author: "Chain Manager",
    icon: "fa-solid fa-store",
  },
  {
    text: "Seamless check-in/check-out process. A must-have for any hotel.",
    author: "Operations Manager",
    icon: "fa-solid fa-hotel",
  },
  {
    text: "Posease makes real-time sales tracking incredibly simple.",
    author: "Accountant",
    icon: "fa-solid fa-chart-line",
  },
  {
    text: "Posease's centralized system is ideal for our franchise needs.",
    author: "Business Owner",
    icon: "fa-solid fa-briefcase",
  },
];

function HotelList() {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={`${styles.wrap} ${styles.headerInner}`}>
          <Link to="/" className={styles.brand} aria-label="PosEase home">
            <img src={logoDark} alt="PosEase" className={styles.brandLogo} />
          </Link>
          <a href={CONTACT_US_URL} className={styles.headerBtn}>
            <i className="fa-solid fa-headset" aria-hidden="true"></i>
            <span>Contact Us</span>
          </a>
        </div>
      </header>

      <main>
        <section className={styles.hero}>
          <div className={`${styles.wrap} ${styles.heroInner}`}>
            <div className={styles.heroContent}>
              <span className={styles.eyebrow}>
                <i className="fa-solid fa-hotel" aria-hidden="true"></i>
                Direct hotel booking
              </span>
              <h1 className={styles.heroTitle}>Booking Engine</h1>
              <p className={styles.heroText}>
                Enhance your direct bookings with our powerful Booking Engine.
                Offer a seamless reservation experience on your website with
                real-time availability and instant confirmation. Customize
                booking options, manage promotions, and reduce dependency on
                third-party platforms to maximize your revenue.
              </p>
              <ul className={styles.highlights}>
                {HERO_HIGHLIGHTS.map((item) => (
                  <li key={item.label}>
                    <span className={styles.highlightIcon} aria-hidden="true">
                      <i className={item.icon}></i>
                    </span>
                    {item.label}
                  </li>
                ))}
              </ul>
              <a href={CONTACT_US_URL} className={styles.demoBtn}>
                <i className="fa-regular fa-calendar" aria-hidden="true"></i>
                Take a free Demo
                <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
              </a>
            </div>
            <div className={styles.heroImageWrap}>
              <img
                src={heroImage}
                alt="Guest booking a hotel room on a phone"
                className={styles.heroImage}
              />
              <span className={`${styles.floatBadge} ${styles.floatTop}`}>
                <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
                Booking confirmed
              </span>
              <span className={`${styles.floatBadge} ${styles.floatBottom}`}>
                <i className="fa-solid fa-shield-halved" aria-hidden="true"></i>
                Secure payment
              </span>
            </div>
          </div>
        </section>

        <section className={`${styles.section} ${styles.featureSection}`}>
          <div className={styles.wrap}>
            <div className={styles.sectionHead}>
              <span className={styles.eyebrow}>
                <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
                Features
              </span>
              <h2 className={styles.sectionTitle}>
                Simplify Direct Bookings with Powerful Booking Engine
              </h2>
            </div>
            <div className={styles.featureGrid}>
              {FEATURES.map((feature) => (
                <article key={feature.title} className={styles.featureCard}>
                  <div
                    className={styles.featureImageWrap}
                    style={{ background: feature.background }}
                  >
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className={styles.featureImage}
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.featureBody}>
                    <div className={styles.featureTitle}>
                      <span className={styles.featureIcon} aria-hidden="true">
                        <i className={feature.icon}></i>
                      </span>
                      <h3>{feature.title}</h3>
                    </div>
                    <p>{feature.text}</p>
                    <ul className={styles.featurePoints}>
                      {feature.points.map((point) => (
                        <li key={point}>
                          <i
                            className="fa-solid fa-circle-check"
                            aria-hidden="true"
                          ></i>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={`${styles.section} ${styles.testimonialSection}`}>
          <div className={styles.wrap}>
            <div className={styles.sectionHead}>
              <span className={styles.eyebrow}>
                <i className="fa-solid fa-heart" aria-hidden="true"></i>
                Testimonials
              </span>
              <h2 className={styles.sectionTitle}>
                Trusted by Thousand of Happy Customers
              </h2>
            </div>
            <div className={styles.testimonials}>
              <div className={styles.testimonialsIntro}>
                <h3>Do not take our word for it. See what our clients say.</h3>
                <p>
                  See how Posease has transformed operations with streamlined
                  billing and advanced features. Our clients’ feedback
                  highlights our ease, efficiency, and innovation.
                </p>
                <a href={CONTACT_US_URL} className={styles.introLink}>
                  Talk to our team
                  <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </a>
              </div>
              <div className={styles.testimonialGrid}>
                {TESTIMONIALS.map((testimonial) => (
                  <figure
                    key={testimonial.author}
                    className={styles.testimonialCard}
                  >
                    <i
                      className={`fa-solid fa-quote-left ${styles.quoteIcon}`}
                      aria-hidden="true"
                    ></i>
                    <blockquote>{testimonial.text}</blockquote>
                    <figcaption>
                      <span className={styles.authorIcon} aria-hidden="true">
                        <i className={testimonial.icon}></i>
                      </span>
                      {testimonial.author}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={`${styles.wrap} ${styles.footerInner}`}>
          <div className={styles.footerBrand}>
            <img src={logoLight} alt="PosEase" className={styles.footerLogo} />
            <p>Fast, easy and secure hotel booking powered by PosEase.</p>
          </div>
          <div className={styles.footerContact}>
            <p className={styles.footerHeading}>Need help?</p>
            <a href={`tel:${CONTACT_MOBILE}`}>
              <span className={styles.footerIcon} aria-hidden="true">
                <i className="fa-solid fa-phone"></i>
              </span>
              {CONTACT_MOBILE_LABEL}
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`}>
              <span className={styles.footerIcon} aria-hidden="true">
                <i className="fa-regular fa-envelope"></i>
              </span>
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
        <div className={`${styles.wrap} ${styles.footerBottom}`}>
          <p>© {new Date().getFullYear()} PosEase. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default HotelList;
